import { clampDateTo, datePl, type MeDto, VEHICLE_FUELS, VEHICLE_OWNERSHIP, type VehicleDto, type VehicleFuel, type VehicleInput, type VehicleOwnership } from '@bqcrm/shared';
import { Anchor, Badge, Button, Card, Group, Modal, Select, SimpleGrid, Stack, Table, Text, Textarea, TextInput, Tooltip } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconAlertTriangle, IconCar, IconPlus } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { CompanyBadgeById, ObligationStateBadge, VehicleAvailabilityBadge, VehicleStatusBadge } from '../../components/Badges';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { EmptyState } from '../../components/ui';
import { useCompanies } from '../../data';
import { notifyError, notifyOk } from '../../notify';
import { FleetShell, kmPl, useFleetInvalidate, useFleetSettings, useFleetVehicles } from './common';

/** Firmy, w których pracownik działa (wybór firmy pojazdu, filtry). */
export function useMyCompanies(me: MeDto) {
  const companies = useCompanies();
  return (companies.data ?? []).filter((c) => me.companyIds.includes(c.id));
}

type Form = Required<{ [K in keyof VehicleInput]: Exclude<VehicleInput[K], undefined> }>;

const emptyForm = (companyId: string | null): Form => ({
  companyId: companyId ?? '',
  plate: '',
  make: '',
  model: '',
  vin: '',
  kind: '',
  fuel: null,
  ownership: 'wlasny',
  financier: '',
  contractNumber: '',
  useFrom: null,
  useTo: null,
  note: '',
});

/** Nowy pojazd albo edycja danych (numer rejestracyjny zmienia się osobno — z historią numerów). */
export function VehicleFormModal({ me, vehicle, onClose, onSaved }: { me: MeDto; vehicle?: VehicleDto; onClose: () => void; onSaved?: (v: VehicleDto) => void }) {
  const phone = useMediaQuery('(max-width: 48em)');
  const companies = useMyCompanies(me);
  const settings = useFleetSettings();
  const invalidate = useFleetInvalidate();
  const [f, setF] = useState<Form>(() =>
    vehicle
      ? { companyId: vehicle.companyId, plate: vehicle.plate, make: vehicle.make, model: vehicle.model, vin: vehicle.vin, kind: vehicle.kind, fuel: vehicle.fuel, ownership: vehicle.ownership, financier: vehicle.financier, contractNumber: vehicle.contractNumber, useFrom: vehicle.useFrom, useTo: vehicle.useTo, note: vehicle.note }
      : emptyForm(companies.length === 1 ? companies[0]!.id : null),
  );
  const set = (patch: Partial<Form>) => setF((p) => ({ ...p, ...patch }));
  const save = useMutation({
    mutationFn: () => {
      if (vehicle) {
        const { plate: _plate, ...rest } = f;
        return api<VehicleDto>(`/fleet/vehicles/${vehicle.id}`, { method: 'PUT', body: rest });
      }
      return api<VehicleDto>('/fleet/vehicles', { method: 'POST', body: f });
    },
    onSuccess: (v) => {
      notifyOk(vehicle ? 'Zapisano dane pojazdu' : `Dodano pojazd ${v.plate}`);
      invalidate();
      onSaved?.(v);
      onClose();
    },
    onError: notifyError,
  });
  const kinds = settings.data?.vehicleKinds ?? [];
  const valid = !!f.companyId && (vehicle || f.plate.trim().length >= 2);
  return (
    <Modal opened onClose={onClose} title={vehicle ? 'Edycja pojazdu' : 'Nowy pojazd'} size="lg" fullScreen={phone}>
      <Stack gap="sm">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <Select label="Firma" required data={companies.map((c) => ({ value: c.id, label: c.shortName }))} value={f.companyId || null} onChange={(v) => set({ companyId: v ?? '' })} allowDeselect={false} />
          {vehicle ? (
            <TextInput label="Numer rejestracyjny" value={f.plate} disabled description="Zmiana numeru — „Zmień numer” na karcie pojazdu" />
          ) : (
            <TextInput label="Numer rejestracyjny" required value={f.plate} onChange={(e) => set({ plate: e.currentTarget.value.toUpperCase() })} data-autofocus />
          )}
          <TextInput label="Marka" value={f.make} onChange={(e) => set({ make: e.currentTarget.value })} />
          <TextInput label="Model" value={f.model} onChange={(e) => set({ model: e.currentTarget.value })} />
          <TextInput label="VIN" maxLength={17} value={f.vin} onChange={(e) => set({ vin: e.currentTarget.value.toUpperCase() })} />
          <Select label="Rodzaj / przeznaczenie" clearable data={[...new Set([...kinds, ...(f.kind ? [f.kind] : [])])]} value={f.kind || null} onChange={(v) => set({ kind: v ?? '' })} />
          <Select label="Paliwo / napęd" clearable data={Object.entries(VEHICLE_FUELS).map(([value, label]) => ({ value, label }))} value={f.fuel} onChange={(v) => set({ fuel: (v as VehicleFuel | null) ?? null })} />
          <Select label="Forma użytkowania" data={Object.entries(VEHICLE_OWNERSHIP).map(([value, label]) => ({ value, label }))} value={f.ownership} onChange={(v) => v && set({ ownership: v as VehicleOwnership })} allowDeselect={false} />
          {f.ownership !== 'wlasny' && (
            <>
              <TextInput label="Finansujący / wynajmujący" value={f.financier} onChange={(e) => set({ financier: e.currentTarget.value })} />
              <TextInput label="Numer umowy" value={f.contractNumber} onChange={(e) => set({ contractNumber: e.currentTarget.value })} />
            </>
          )}
          <TextInput label="Użytkowanie od" type="date" value={f.useFrom ?? ''} onChange={(e) => {
            const v = e.currentTarget.value || null;
            set({ useFrom: v, useTo: v ? clampDateTo(v, f.useTo ?? '', { keepEmpty: true }) || null : f.useTo });
          }} />
          <TextInput label="Użytkowanie do" description="puste — bez końca" type="date" min={f.useFrom ?? undefined} value={f.useTo ?? ''} onChange={(e) => set({ useTo: e.currentTarget.value || null })} />
        </SimpleGrid>
        <Textarea label="Uwagi" autosize minRows={2} value={f.note} onChange={(e) => set({ note: e.currentTarget.value })} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
            {vehicle ? 'Zapisz' : 'Dodaj pojazd'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Pojazdy floty: tabela z filtrami (firma, przypisanie, dostępność, status). */
export function VehiclesPage({ me }: { me: MeDto }) {
  const q = useFleetVehicles();
  const companies = useMyCompanies(me);
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [holder, setHolder] = useState<string | null>(null);
  const [avail, setAvail] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>('aktywny');
  const [adding, setAdding] = useState(false);
  const rows = (q.data ?? []).filter(
    (v) =>
      (!companyId || v.companyId === companyId) &&
      (!holder || (holder === 'brak' ? !v.assignment : v.assignment?.holder === holder)) &&
      (!avail || (avail === 'niedostepny' ? !!v.unavailable : !v.unavailable)) &&
      (!status || v.status === status),
  );
  return (
    <FleetShell me={me}>
      <PageHeader
        title="Pojazdy"
        description="Samochody firmowe: przypisanie, dostępność, najbliższa czynność i ostatni znany przebieg. Pojazdów się nie usuwa — wycofany zostaje w historii i raportach."
        actions={
          can(me, 'flota.zarzadzanie') && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setAdding(true)}>
              Dodaj pojazd
            </Button>
          )
        }
      />
      <Group mb="md" gap="xs" wrap="wrap">
        {companies.length > 1 && <Select placeholder="Wszystkie firmy" clearable data={companies.map((c) => ({ value: c.id, label: c.shortName }))} value={companyId} onChange={setCompanyId} w={{ base: '100%', sm: 200 }} />}
        <Select
          placeholder="Przypisanie: wszystkie"
          clearable
          data={[
            { value: 'osoba', label: 'Do osoby' },
            { value: 'ekipa', label: 'Do ekipy' },
            { value: 'brak', label: 'Bez przypisania' },
          ]}
          value={holder}
          onChange={setHolder}
          w={{ base: '100%', sm: 200 }}
        />
        <Select
          placeholder="Dostępność: wszystkie"
          clearable
          data={[
            { value: 'dostepny', label: 'Dostępne' },
            { value: 'niedostepny', label: 'Niedostępne' },
          ]}
          value={avail}
          onChange={setAvail}
          w={{ base: '100%', sm: 200 }}
        />
        <Select
          placeholder="Status: wszystkie"
          clearable
          data={[
            { value: 'aktywny', label: 'Aktywne' },
            { value: 'wycofany', label: 'Wycofane' },
          ]}
          value={status}
          onChange={setStatus}
          w={{ base: '100%', sm: 200 }}
        />
      </Group>
      <Card padding={0}>
        <QueryState isLoading={q.isLoading} error={q.error}>
          {rows.length ? (
            <Table.ScrollContainer minWidth={1100}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Numer</Table.Th>
                    <Table.Th>Marka i model</Table.Th>
                    <Table.Th>Firma</Table.Th>
                    <Table.Th>Przypisanie</Table.Th>
                    <Table.Th>Dostępność</Table.Th>
                    <Table.Th>Najbliższa czynność</Table.Th>
                    <Table.Th ta="right">Ostatni przebieg</Table.Th>
                    <Table.Th ta="right">Zgłoszenia</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((v) => (
                    <Table.Tr key={v.id} className="row-link" onClick={() => navigate(`/flota/pojazdy/${v.id}`)} style={v.status === 'wycofany' ? { opacity: 0.6 } : undefined}>
                      <Table.Td>
                        <Anchor component={Link} to={`/flota/pojazdy/${v.id}`} fw={700} size="sm" onClick={(e) => e.stopPropagation()}>
                          {v.plate}
                        </Anchor>
                        {v.status === 'wycofany' && (
                          <div>
                            <VehicleStatusBadge status={v.status} size="xs" />
                          </div>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{[v.make, v.model].filter(Boolean).join(' ') || '—'}</Text>
                        {v.kind && (
                          <Text size="xs" c="dimmed">
                            {v.kind}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <CompanyBadgeById id={v.companyId} />
                      </Table.Td>
                      <Table.Td>
                        {v.assignment ? (
                          <>
                            <Group gap={4} wrap="nowrap">
                              <Text size="sm">{v.assignment.label}</Text>
                              {v.assignment.leaderChanged && (
                                <Tooltip label="Ekipa ma nowego lidera — potwierdź odpowiedzialnego">
                                  <IconAlertTriangle size={16} color="var(--mantine-color-orange-6)" />
                                </Tooltip>
                              )}
                            </Group>
                            <Text size="xs" c="dimmed">
                              odpowiedzialny: {v.assignment.responsibleName}
                            </Text>
                          </>
                        ) : (
                          <Text size="sm" c="dimmed">
                            bez przypisania
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <VehicleAvailabilityBadge unavailable={v.unavailable} />
                      </Table.Td>
                      <Table.Td>
                        {v.next ? (
                          <>
                            <Group gap={6} wrap="nowrap">
                              <Text size="sm">{v.next.typeName}</Text>
                              <ObligationStateBadge state={v.next.state} reason={v.next.reason} size="xs" />
                            </Group>
                            <Text size="xs" c="dimmed">
                              {[v.next.dueDate ? datePl(v.next.dueDate) : null, v.next.dueKm !== null ? kmPl(v.next.dueKm) : null].filter(Boolean).join(' / ') || v.next.reason}
                            </Text>
                          </>
                        ) : (
                          <Text size="sm" c="dimmed">
                            —
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td ta="right">
                        {v.odometer ? (
                          <>
                            <Text size="sm" className="tabular">
                              {kmPl(v.odometer.km)}
                            </Text>
                            <Group gap={4} justify="flex-end" wrap="nowrap">
                              <Text size="xs" c="dimmed">
                                {datePl(v.odometer.readAt)}
                              </Text>
                              {v.odometer.stale && (
                                <Badge size="xs" color="yellow" variant="light">
                                  nieaktualny
                                </Badge>
                              )}
                            </Group>
                          </>
                        ) : (
                          <Text size="sm" c="dimmed">
                            —
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td ta="right">
                        {v.openReports ? (
                          <Badge size="sm" color={v.urgentReports ? 'red' : 'blue'} variant={v.urgentReports ? 'filled' : 'light'}>
                            {v.openReports}
                            {v.urgentReports ? ` (pilne: ${v.urgentReports})` : ''}
                          </Badge>
                        ) : (
                          '—'
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          ) : (
            <EmptyState
              icon={<IconCar size={24} />}
              title={q.data?.length ? 'Brak pojazdów w wybranym filtrze' : 'Brak pojazdów'}
              hint={q.data?.length ? 'Zmień filtry, aby zobaczyć pozostałe pojazdy.' : 'Dodaj pierwszy samochód firmowy — potem przypisz go do osoby lub ekipy i wpisz terminy.'}
            />
          )}
        </QueryState>
      </Card>
      {adding && <VehicleFormModal me={me} onClose={() => setAdding(false)} onSaved={(v) => navigate(`/flota/pojazdy/${v.id}`)} />}
    </FleetShell>
  );
}
